import { useTranslation } from "react-i18next";
import { Errors, useForm } from "hooks/useForm";
import { TextInput } from "components/inputs/text-input/TextInput";
import { ProfileBindingModel } from "@switcherstudio/switcher-api-client";
import styles from "./index.module.scss";
import { PhoneNumberInput } from "components/inputs/text-input/PhoneNumberInput";
import { isBasicUrl } from "helpers/strings";
import { Button } from "components/buttons/Button";
import { useMemo } from "react";

export interface EditProfileFormProps {
    onSubmit: (payload: ProfileBindingModel) => Promise<void>;
    profile: ProfileBindingModel;
    loading?: boolean;
}

export const EditProfileForm: React.FC<EditProfileFormProps> = ({
    onSubmit,
    profile,
    loading = false
}) => {
    const { t } = useTranslation();
    const validate = (values: ProfileBindingModel) => {
        const errors: Errors = {};

        if (!values.Email) {
            errors.Email = t("forgot-password:enter-email");
        } else if (!/\S+@\S+\.\S+/.test(values.Email)) {
            errors.Email = t("errors:invalid-email");
        }

        if (!!values.WebsiteUrl && !isBasicUrl(values.WebsiteUrl)) {
            errors.WebsiteUrl = t("errors:invalid-url");
        }

        return errors;
    };

    const {
        values,
        handleChange,
        handleSubmit,
        errors,
        isSubmitting,
        hasErrors
    } = useForm<ProfileBindingModel>(profile, onSubmit, validate, false, true);

    const hasChanges = useMemo(() => {
        const formFields = Object.keys(values);
        return !formFields.every((field) => values[field] === profile[field]);
    }, [profile, values]);

    const onFormSubmit = async (event: React.FormEvent<HTMLFormElement>) => {
        event.preventDefault();
        await handleSubmit();
    };

    return (
        <form className={styles["profile-form"]} onSubmit={onFormSubmit}>
            <div className="row">
                <div className="col-md-6 col-12">
                    <TextInput
                        id="FirstName"
                        type="text"
                        value={values.FirstName}
                        error={errors.FirstName}
                        onChange={handleChange}
                        label={t("create-account:first-name")}
                    />
                    <TextInput
                        id="LastName"
                        type="text"
                        value={values.LastName}
                        error={errors.LastName}
                        onChange={handleChange}
                        label={t("create-account:last-name")}
                    />
                </div>

                <div className="col-md-6 col-12">
                    <TextInput
                        id="Email"
                        type="email"
                        value={values.Email}
                        error={errors.Email}
                        onChange={handleChange}
                        label={t("create-account:email-address")}
                    />
                    <PhoneNumberInput
                        id="PhoneNumber"
                        value={values.PhoneNumber}
                        error={errors.PhoneNumber}
                        onChange={handleChange}
                        label={t("create-account:phone-number")}
                        placeholder={""}
                        location="profile-page"
                    />
                </div>
            </div>
            <div className="row mt-3">
                <div className="col-12">
                    <TextInput
                        id="Company"
                        type="text"
                        value={values.Company}
                        error={errors.Company}
                        onChange={handleChange}
                        label={t("profile:company")}
                    />
                    <TextInput
                        id="Title"
                        type="text"
                        value={values.Title}
                        error={errors.Title}
                        onChange={handleChange}
                        label={t("profile:title")}
                    />
                    <TextInput
                        id="WebsiteUrl"
                        type="text"
                        value={values.WebsiteUrl}
                        error={errors.WebsiteUrl}
                        onChange={handleChange}
                        label={t("profile:website")}
                    />
                </div>
            </div>
            <div className={styles["button-container"]}>
                <Button
                    type="primary"
                    disabled={
                        isSubmitting || loading || !hasChanges || hasErrors()
                    }
                    onClick={() => handleSubmit()}
                >
                    {t("buttons:save-changes").toUpperCase()}
                </Button>
            </div>
        </form>
    );
};
